import { Row, Col, Card, CardBody, Badge, Progress } from 'reactstrap';
import { PageHeader } from '../../components/PageHeader';
import { DataTable } from '../../components/DataTable';
import { useBulkBoxes } from './useBulkBoxes';

/**
 * Bulk-Massenspeicher-Pool (Hetzner Storage Boxen) — Betreiber-Sicht, nur lesend.
 * Jede Box ist eine k8s-StorageClass; die Belegung ist die Summe der von Pods
 * gebuchten GB. Union-Boxen (mergerfs/rclone) gelten als unbegrenzt und haben
 * keine Kapazitäts-/Auslastungswerte.
 */
const fmt = new Intl.NumberFormat('de-DE');
const gb = (v) => (v == null ? '—' : `${fmt.format(v)} GB`);

function usageColor(pct) {
  if (pct == null) return 'info';
  if (pct >= 90) return 'danger';
  if (pct >= 75) return 'warning';
  return 'success';
}

export function NexusBulkBoxesPage() {
  const boxes = useBulkBoxes();
  const items = boxes.data?.boxes ?? [];

  const singles = items.filter((b) => !b.isUnion);
  const totalCapacity = singles.reduce((s, b) => s + (b.capacityGb || 0), 0);
  const totalUsed = singles.reduce((s, b) => s + (b.usedGb || 0), 0);
  const totalPct = totalCapacity ? Math.round((totalUsed / totalCapacity) * 100) : null;

  return (
    <div>
      <PageHeader title="Bulk-Storage" subtitle="Massenspeicher-Pool (Hetzner Storage Boxen)" />

      <Row className="g-3 mb-3">
        <Col xs="6" md="3">
          <Card className="h-100 border-0 shadow-sm">
            <CardBody className="py-3">
              <div className="text-muted small text-uppercase">Kapazität (ohne Union)</div>
              <div className="fs-4 fw-semibold">{gb(totalCapacity)}</div>
            </CardBody>
          </Card>
        </Col>
        <Col xs="6" md="3">
          <Card className="h-100 border-0 shadow-sm">
            <CardBody className="py-3">
              <div className="text-muted small text-uppercase">Belegt</div>
              <div className="fs-4 fw-semibold">
                {gb(totalUsed)}
                {totalPct != null && <span className="fs-6 text-muted"> ({totalPct} %)</span>}
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <DataTable
        loading={boxes.isLoading}
        error={boxes.error}
        empty={!boxes.isLoading && items.length === 0}
        emptyMessage="Keine Boxen im Pool."
        onRetry={() => boxes.refetch()}
      >
        <thead>
          <tr>
            <th>Name</th>
            <th>Provider</th>
            <th>StorageClass</th>
            <th className="text-end">Kapazität</th>
            <th className="text-end">Reserviert</th>
            <th className="text-end">Belegt</th>
            <th className="text-end">Frei</th>
            <th style={{ minWidth: 160 }}>Auslastung</th>
            <th>Aktiv</th>
          </tr>
        </thead>
        <tbody>
          {items.map((b) => (
            <tr key={b.id}>
              <td className="small">{b.name}</td>
              <td className="small text-muted">{b.provider || '—'}</td>
              <td className="font-monospace small">{b.storageClass}</td>
              <td className="text-end small">{b.isUnion ? <span className="text-muted">∞</span> : gb(b.capacityGb)}</td>
              <td className="text-end small">{b.isUnion ? '—' : gb(b.reservedGb)}</td>
              <td className="text-end small">{gb(b.usedGb)}</td>
              <td className="text-end small">{b.isUnion ? <span className="text-muted">∞</span> : gb(b.freeGb)}</td>
              <td>
                {b.isUnion ? (
                  <Badge color="info" pill>∞/Union</Badge>
                ) : (
                  <div className="d-flex align-items-center gap-2">
                    <Progress value={b.usedPct ?? 0} color={usageColor(b.usedPct)} style={{ flex: 1, minWidth: 80, height: 8 }} />
                    <span className="small text-muted" style={{ width: 40 }}>{b.usedPct == null ? '—' : `${b.usedPct} %`}</span>
                  </div>
                )}
              </td>
              <td>{b.active ? <Badge color="success">aktiv</Badge> : <Badge color="secondary">inaktiv</Badge>}</td>
            </tr>
          ))}
        </tbody>
      </DataTable>
    </div>
  );
}
